import * as Sentry from "@sentry/react";
import { Session, User } from '@supabase/supabase-js';
import React, { useEffect, useState } from 'react';
import { apiClient } from '@copro/api';
import { AuthContext } from './AuthContext';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Récupérer la session initiale
    apiClient.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Écouter les changements d'authentification
    const { data: { subscription } } = apiClient.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await apiClient.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw error;
    }

    setSession(data.session);
    setUser(data.session?.user ?? null);
  };

  const signUp = async (email: string, password: string) => {
    const { data, error } = await apiClient.auth.signUp({
      email,
      password,
    });

    if (error) {
      throw error;
    }

    setSession(data.session);
    setUser(data.session?.user ?? null);
  };

  const signOut = async () => {
    const { error } = await apiClient.auth.signOut();
    if (error) {
      throw error;
    }
  };

  const onSaveAppOpened = async (userId: string) => {
    await apiClient
      .from('user_basic_info')
      .update({ last_app_open_at: new Date().toISOString() })
      .eq('id', userId);
  }

  useEffect(() => {
    if (user?.id) {
      onSaveAppOpened(user.id);

      Sentry.setUser({
        id: user.id,
        email: user.email,
      });
    }
  }, [user?.id]);

  return (
    <AuthContext.Provider value={{ session, user, signIn, signUp, signOut, loading }}>
      {children}
    </AuthContext.Provider>
  );
}
